import type { CliApiClient, CliSubmissionResponse } from "../api/cli-api.js";
import {
  lookupPluginSubmissionId,
  lookupSkillSubmissionId,
  recordPluginPublish,
  recordSkillPublish,
} from "./publish-registry.js";

const SUBMISSION_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SUBMISSION_ID_IN_PATH =
  /\/artifact-submissions\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

function requestBodyMatchesArtifact(body: unknown, artifactId: string): boolean {
  if (!body || typeof body !== "object") return false;
  const record = body as Record<string, unknown>;
  if (record.artifact_id === artifactId) return true;

  if (typeof record.source_url !== "string") return false;
  const url = record.source_url.toLowerCase();
  const id = artifactId.toLowerCase();
  if (url.includes(id)) return true;

  const segments = url.split(/[/\\?&#]/).filter(Boolean);
  return segments.some((segment) => segment === id);
}

function submissionMatchesPlugin(
  state: CliSubmissionResponse,
  pluginId: string,
): boolean {
  const anyState = state as CliSubmissionResponse & { artifact_id?: string };
  if (anyState.artifact_id === pluginId) return true;

  const pluginIdLower = pluginId.toLowerCase();
  const publicUrl = state.publish?.public_url?.toLowerCase();
  if (publicUrl?.includes(`/plugins/${pluginIdLower}`)) return true;
  return false;
}

async function rememberResolvedSubmission(
  submissionId: string,
  platformUrl: string,
  state: CliSubmissionResponse,
): Promise<void> {
  const anyState = state as CliSubmissionResponse & { artifact_id?: string };
  if (!anyState.artifact_id) return;
  await recordPluginPublish({
    submissionId,
    platformUrl,
    pluginId: anyState.artifact_id,
  });
}

export async function resolvePluginSubmissionId(
  client: CliApiClient,
  pluginId: string,
  platformUrl: string,
): Promise<string | undefined> {
  if (SUBMISSION_ID_PATTERN.test(pluginId)) return pluginId;

  const cached = await lookupPluginSubmissionId(pluginId, platformUrl);
  if (cached) return cached;

  const latestSeenAt = new Map<string, string>();
  let cursor: string | undefined;

  for (let page = 0; page < 10; page += 1) {
    const response = await client.getRequestLogs({ page_size: 100, cursor });
    for (const item of response.items) {
      const match = item.path.match(SUBMISSION_ID_IN_PATH);
      if (match) {
        const submissionId = match[1];
        const previous = latestSeenAt.get(submissionId);
        if (!previous || item.created_at > previous) {
          latestSeenAt.set(submissionId, item.created_at);
        }
      }

      if (
        item.method === "POST" &&
        item.path.endsWith("/artifact-submissions") &&
        requestBodyMatchesArtifact(item.request_body, pluginId)
      ) {
        const postMatch = item.path.match(SUBMISSION_ID_IN_PATH);
        if (postMatch) {
          latestSeenAt.set(postMatch[1], item.created_at);
        }
      }
    }
    if (!response.next_cursor) break;
    cursor = response.next_cursor ?? undefined;
  }

  const candidates = [...latestSeenAt.entries()]
    .sort((a, b) => b[1].localeCompare(a[1]))
    .map(([submissionId]) => submissionId)
    .slice(0, 50);

  const pluginIdLower = pluginId.toLowerCase();

  for (const candidate of candidates) {
    try {
      const state = await client.getArtifactSubmission(candidate);
      if (submissionMatchesPlugin(state, pluginId)) {
        await rememberResolvedSubmission(candidate, platformUrl, state);
        return candidate;
      }

      const logs = await client.getArtifactSubmissionLogs(candidate);
      const matched = logs.items.some((item) =>
        item.message.toLowerCase().includes(pluginIdLower),
      );
      if (matched) {
        await rememberResolvedSubmission(candidate, platformUrl, state);
        return candidate;
      }
    } catch {
      continue;
    }
  }

  return undefined;
}

function submissionMatchesSkill(
  state: CliSubmissionResponse,
  skillId: string,
): boolean {
  const anyState = state as CliSubmissionResponse & { artifact_id?: string };
  if (anyState.artifact_id === skillId) return true;

  const skillIdLower = skillId.toLowerCase();
  const publicUrl = state.publish?.public_url?.toLowerCase();
  if (publicUrl?.includes(`/skills/${skillIdLower}`)) return true;
  return false;
}

async function rememberResolvedSkillSubmission(
  submissionId: string,
  platformUrl: string,
  state: CliSubmissionResponse,
): Promise<void> {
  const anyState = state as CliSubmissionResponse & { artifact_id?: string };
  if (!anyState.artifact_id) return;
  await recordSkillPublish({
    submissionId,
    platformUrl,
    skillId: anyState.artifact_id,
  });
}

export async function resolveSkillSubmissionId(
  client: CliApiClient,
  skillId: string,
  platformUrl: string,
): Promise<string | undefined> {
  if (SUBMISSION_ID_PATTERN.test(skillId)) return skillId;

  const cached = await lookupSkillSubmissionId(skillId, platformUrl);
  if (cached) return cached;

  const latestSeenAt = new Map<string, string>();
  let cursor: string | undefined;

  for (let page = 0; page < 10; page += 1) {
    const response = await client.getRequestLogs({ page_size: 100, cursor });
    for (const item of response.items) {
      const match = item.path.match(SUBMISSION_ID_IN_PATH);
      if (match) {
        const submissionId = match[1];
        const previous = latestSeenAt.get(submissionId);
        if (!previous || item.created_at > previous) {
          latestSeenAt.set(submissionId, item.created_at);
        }
      }

      if (
        item.method === "POST" &&
        item.path.endsWith("/artifact-submissions") &&
        requestBodyMatchesArtifact(item.request_body, skillId)
      ) {
        const postMatch = item.path.match(SUBMISSION_ID_IN_PATH);
        if (postMatch) {
          latestSeenAt.set(postMatch[1], item.created_at);
        }
      }
    }
    if (!response.next_cursor) break;
    cursor = response.next_cursor ?? undefined;
  }

  const candidates = [...latestSeenAt.entries()]
    .sort((a, b) => b[1].localeCompare(a[1]))
    .map(([submissionId]) => submissionId)
    .slice(0, 50);

  const skillIdLower = skillId.toLowerCase();

  for (const candidate of candidates) {
    try {
      const state = await client.getArtifactSubmission(candidate);
      if (submissionMatchesSkill(state, skillId)) {
        await rememberResolvedSkillSubmission(candidate, platformUrl, state);
        return candidate;
      }

      const logs = await client.getArtifactSubmissionLogs(candidate);
      const matched = logs.items.some((item) =>
        item.message.toLowerCase().includes(skillIdLower),
      );
      if (matched) {
        await rememberResolvedSkillSubmission(candidate, platformUrl, state);
        return candidate;
      }
    } catch {
      continue;
    }
  }

  return undefined;
}
